/**
 * ToolManager - Central mediator for all toolbar tools
 * Manages tool lifecycle, settings persistence, and shared page-level CSS
 */

import type { Tool, ToolId, ToolbarSettings, PageThemeId, FontFamilyId, WordHighlightEvent, VoiceId } from '../types/config';
import { normalizeVoice } from '../types/config';
import type { ToolUsageTracker } from '../features/ToolUsageTracker';
import { N_ } from '../i18n';

/** Origin serving widget assets: the API endpoint's origin (https://readingvox.com/api/v1 → https://readingvox.com) */
function fontOrigin(apiEndpoint: string | undefined): string {
  try { return new URL(apiEndpoint || 'https://readingvox.com/api/v1', location.href).origin; } catch { return 'https://readingvox.com'; }
}

const STORAGE_KEY = 'rv-toolbar-settings';
const STYLE_ID = 'rv-page-overrides';

const DEFAULT_SETTINGS: ToolbarSettings = {
  pageTheme: 'default',
  fontSize: 100,
  fontFamily: 'default',
  maskHeight: 4,
  autoScroll: true,
  toolbarTheme: 'auto',
  highlightMode: 'wordsent',
  highlightWordColor: 'navy',
  highlightSentenceColor: 'yellow',
  // No default voice: the publisher's config.voice (data-voice) applies until the
  // visitor explicitly picks one in Settings.
};

// Voice options — primary voices shown by default, others in settings
const VOICE_OPTIONS: { id: VoiceId; name: string; desc: string; gender: 'male' | 'female'; primary?: boolean }[] = [
  { id: 'heart', name: 'Heart', desc: N_('Gentle'), gender: 'female', primary: true },
  { id: 'michael', name: 'Michael', desc: N_('Deep'), gender: 'male', primary: true },
  { id: 'lily', name: 'Lily', desc: N_('Bright'), gender: 'female' },
  { id: 'leo', name: 'Leo', desc: N_('Casual'), gender: 'male' },
  { id: 'mia', name: 'Mia', desc: N_('Young'), gender: 'female' },
  { id: 'theo', name: 'Theo', desc: N_('British'), gender: 'male' },
];

// Standard voices (Free and Starter plans), shown when /widget/config says so
const STANDARD_VOICE_OPTIONS: typeof VOICE_OPTIONS = [
  { id: 'ava', name: 'Ava', desc: N_('Clear'), gender: 'female', primary: true },
  { id: 'sam', name: 'Sam', desc: N_('Steady'), gender: 'male', primary: true },
];

// Page theme CSS definitions
const PAGE_THEMES: Record<PageThemeId, { bg: string; text: string; label: string }> = {
  'default': { bg: 'inherit', text: 'inherit', label: N_('Default') },
  'black-on-white': { bg: '#ffffff', text: '#000000', label: N_('Black on White') },
  'white-on-black': { bg: '#1a1a1a', text: '#f0f0f0', label: N_('White on Black') },
  'black-on-yellow': { bg: '#ffff99', text: '#000000', label: N_('Black on Yellow') },
  'black-on-light-blue': { bg: '#cfe8ff', text: '#000000', label: N_('Black on Light Blue') },
  'black-on-pink': { bg: '#fbd0e4', text: '#000000', label: N_('Black on Pink') },
  'black-on-cream': { bg: '#fdf6e3', text: '#1a1a1a', label: N_('Black on Cream') },
  'blue-on-yellow': { bg: '#fffde7', text: '#0d47a1', label: N_('Blue on Yellow') },
  'blue-on-white': { bg: '#ffffff', text: '#0d47a1', label: N_('Blue on White') },
  'blue-on-pink': { bg: '#fbd0e4', text: '#0d3a8a', label: N_('Blue on Pink') },
  'red-on-white': { bg: '#ffffff', text: '#a50000', label: N_('Red on White') },
  'yellow-on-blue': { bg: '#0d47a1', text: '#ffff00', label: N_('Yellow on Blue') },
  'yellow-on-black': { bg: '#000000', text: '#ffff00', label: N_('Yellow on Black') },
};

/** Display order for page color pickers */
const THEME_ORDER: PageThemeId[] = [
  'default', 'black-on-white', 'white-on-black', 'black-on-cream', 'black-on-yellow', 'black-on-light-blue',
  'black-on-pink', 'blue-on-white', 'blue-on-yellow', 'blue-on-pink', 'red-on-white', 'yellow-on-blue', 'yellow-on-black',
];

// Font family CSS values
const FONT_FAMILIES: Record<FontFamilyId, { css: string; label: string }> = {
  'default': { css: 'inherit', label: N_('Default') },
  'sans-serif': { css: 'Arial, Helvetica, sans-serif', label: N_('Sans-serif') },
  'serif': { css: "Georgia, 'Times New Roman', serif", label: N_('Serif') },
  'monospace': { css: "'Courier New', Courier, monospace", label: N_('Monospace') },
  'comic-sans': { css: "'Comic Sans MS', 'Comic Sans', cursive", label: 'Comic Sans' },
  'opendyslexic': { css: '"OpenDyslexicRegular", sans-serif', label: 'OpenDyslexic' },
};

export { PAGE_THEMES, THEME_ORDER, FONT_FAMILIES, VOICE_OPTIONS, STANDARD_VOICE_OPTIONS };

type SettingsCallback = (settings: ToolbarSettings) => void;

export class ToolManager {
  private tools: Map<ToolId, Tool> = new Map();
  private settings: ToolbarSettings;
  private styleElement: HTMLStyleElement | null = null;
  private openDropdown: ToolId | null = null;
  private listeners: SettingsCallback[] = [];
  private wordHighlightListeners: ((event: WordHighlightEvent) => void)[] = [];
  private dyslexicLoaded: boolean = false;
  private usageTracker: ToolUsageTracker | null = null;

  /** API base (assets like fonts are served from its origin) */
  apiEndpoint?: string;

  constructor(apiEndpoint?: string) {
    this.apiEndpoint = apiEndpoint;
    this.settings = this.loadSettings();
  }

  /**
   * Initialize — inject style element, apply saved settings
   */
  init(): void {
    this.ensureStyleElement();
    this.applyPageStyles();
  }

  /**
   * Set the usage tracker for tool analytics
   */
  setUsageTracker(tracker: ToolUsageTracker): void {
    this.usageTracker = tracker;
  }

  /**
   * Track an action-button click (dictionary, translate) that bypasses toggleTool
   */
  trackToolAction(toolId: string): void {
    this.usageTracker?.trackActivate(toolId);
  }

  /**
   * Register a tool
   */
  registerTool(tool: Tool): void {
    this.tools.set(tool.id, tool);
  }

  /**
   * Get a registered tool
   */
  getTool(id: ToolId): Tool | undefined {
    return this.tools.get(id);
  }

  /**
   * Toggle a tool on/off.
   * Dropdown tools (pageTheme, fontSize, etc.) are mutually exclusive.
   * Overlay tools (simplify, screenMask, magnifier) can coexist with each other and with dropdowns.
   */
  toggleTool(id: ToolId): void {
    const tool = this.tools.get(id);
    if (!tool) return;

    if (tool.isActive) {
      tool.deactivate();
      if (this.openDropdown === id) this.openDropdown = null;
      this.usageTracker?.trackDeactivate(id);
    } else {
      // Only close previous dropdown if this is also a dropdown (not an overlay)
      if (!tool.isOverlay && this.openDropdown && this.openDropdown !== id) {
        const openTool = this.tools.get(this.openDropdown);
        if (openTool?.isActive && !openTool.isOverlay) openTool.deactivate();
        this.openDropdown = null;
      }

      tool.activate();
      if (tool.isToggleable && !tool.isOverlay) this.openDropdown = id;
      this.usageTracker?.trackActivate(id);
    }
  }

  /**
   * Close all dropdown tools (not overlays like simplify/mask/magnifier)
   */
  closeAllTools(): void {
    this.tools.forEach(tool => {
      if (tool.isActive && !tool.isOverlay) tool.deactivate();
    });
    this.openDropdown = null;
  }

  /**
   * Destroy all tools (overlays included) — called on widget destroy
   */
  deactivateAll(): void {
    this.tools.forEach(tool => {
      if (tool.isActive) tool.deactivate();
    });
    this.openDropdown = null;
  }

  /** Voice in use when the visitor hasn't picked one (the publisher's config.voice) */
  private defaultVoice: VoiceId = 'heart';

  setDefaultVoice(voice: VoiceId | undefined): void {
    if (voice) this.defaultVoice = normalizeVoice(voice);
  }

  /** Voice to show as selected: the visitor's choice, else the publisher's */
  getEffectiveVoice(): VoiceId {
    return this.settings.voice || this.defaultVoice;
  }

  /**
   * Get current settings
   */
  getSettings(): ToolbarSettings {
    return { ...this.settings };
  }

  /**
   * Update settings and re-apply page styles
   */
  updateSettings(partial: Partial<ToolbarSettings>): void {
    this.settings = { ...this.settings, ...partial };
    if (partial.voice) this.settings.voiceChosen = true;
    this.saveSettings();
    this.applyPageStyles();
    this.notifyListeners();

    // Track setting changes
    if (this.usageTracker) {
      for (const [key, value] of Object.entries(partial)) {
        const toolId = this.settingToToolId(key);
        this.usageTracker.trackSettingChange(toolId, key, String(value));
      }
    }
  }

  private settingToToolId(setting: string): string {
    const map: Record<string, string> = {
      pageTheme: 'pageTheme',
      fontSize: 'fontSize',
      fontFamily: 'fontSelector',
      maskHeight: 'screenMask',
      toolbarTheme: 'settings',
      voice: 'settings',
      autoScroll: 'settings',
    };
    return map[setting] || 'settings';
  }

  /**
   * Reset all settings to defaults
   */
  resetSettings(): void {
    this.settings = { ...DEFAULT_SETTINGS };
    this.saveSettings();
    this.applyPageStyles();
    this.notifyListeners();
  }

  /**
   * Subscribe to settings changes
   */
  onSettingsChanged(callback: SettingsCallback): void {
    this.listeners.push(callback);
  }

  /**
   * Subscribe to word highlight events
   */
  onWordHighlight(callback: (event: WordHighlightEvent) => void): void {
    this.wordHighlightListeners.push(callback);
  }

  /**
   * Emit a word highlight event (called by ReadingVox during playback)
   */
  emitWordHighlight(event: WordHighlightEvent): void {
    this.wordHighlightListeners.forEach(cb => cb(event));
  }

  /**
   * Load OpenDyslexic, self-hosted next to the widget (no third-party requests).
   * Fonts are served with CORS from /widget/v1/fonts on the API origin.
   */
  async loadDyslexicFont(): Promise<void> {
    if (this.dyslexicLoaded) return;

    const base = `${fontOrigin(this.apiEndpoint)}/widget/v1/fonts`;
    const style = document.createElement('style');
    style.id = 'rv-opendyslexic';
    style.textContent = ` @font-face { font-family: 'OpenDyslexicRegular'; font-weight: normal; font-display: swap; src: url('${base}/OpenDyslexic-Regular.woff') format('woff'); } @font-face { font-family: 'OpenDyslexicRegular'; font-weight: bold; font-display: swap; src: url('${base}/OpenDyslexic-Bold.woff') format('woff'); }`;
    document.head.appendChild(style);

    // Wait for font to load
    await new Promise<void>((resolve) => {
      document.fonts?.load("16px 'OpenDyslexicRegular'").then(() => resolve(), () => resolve());
      setTimeout(resolve, 3000); // Timeout after 3s
    });

    this.dyslexicLoaded = true;
  }

  /**
   * Apply all page-level styles from current settings
   */
  applyPageStyles(): void {
    this.ensureStyleElement();
    if (!this.styleElement) return;

    const rules: string[] = [];
    const { pageTheme, fontSize, fontFamily } = this.settings;
    const textSpacing = Math.max(0, Math.min(4, this.settings.textSpacing || 0));

    // Exclusion selector — prevent RV widget elements from being styled
    // Uses attribute selector on class containing "rv-"
    const exclude = ':not([class*="rv-"])';

    // Page theme
    if (pageTheme !== 'default') {
      const theme = PAGE_THEMES[pageTheme];
      rules.push(` html.rv-theme-active, html.rv-theme-active body { background-color: ${theme.bg} !important; color: ${theme.text} !important; } html.rv-theme-active body *${exclude} { background-color: ${theme.bg} !important; color: ${theme.text} !important; } html.rv-theme-active body a${exclude} { color: ${theme.text} !important; } /* Isolate ReadingVox widget UI from page theme overrides. Elements without rv-* classes inside rv-* containers inherit from their parent (which has explicit toolbar-theme colors). */ html.rv-theme-active body .rv-toolbar *${exclude}, html.rv-theme-active body .rv-tb-dropdown *${exclude}, html.rv-theme-active body .rv-toggle-btn *${exclude}, html.rv-theme-active body .rv-magnifier-panel *${exclude}, html.rv-theme-active body .rv-word-input-overlay *${exclude} { background-color: inherit !important; color: inherit !important; } `);
      document.documentElement.classList.add('rv-theme-active');
    } else {
      document.documentElement.classList.remove('rv-theme-active');
    }

    // Text spacing — level 4 matches WCAG 1.4.12 (letter 0.12em, word 0.16em, line 2)
    if (textSpacing > 0) {
      rules.push(` html.rv-spacing-active body *${exclude} { letter-spacing: ${(0.03 * textSpacing).toFixed(2)}em !important; word-spacing: ${(0.04 * textSpacing).toFixed(2)}em !important; line-height: ${(1.5 + 0.125 * textSpacing).toFixed(3)} !important; } html.rv-spacing-active body p${exclude} { margin-bottom: ${(1 + 0.25 * textSpacing).toFixed(2)}em !important; } /* Keep ReadingVox's own UI at its normal spacing */ html.rv-spacing-active body .rv-toolbar *${exclude}, html.rv-spacing-active body .rv-tb-dropdown *${exclude}, html.rv-spacing-active body .rv-toggle-btn *${exclude}, html.rv-spacing-active body .rv-magnifier-panel *${exclude}, html.rv-spacing-active body .rv-word-input-overlay *${exclude}, html.rv-spacing-active body .rv-dictionary-popup *${exclude}, html.rv-spacing-active body .rv-translation-popup *${exclude}, html.rv-spacing-active body .rv-context-menu *${exclude} { letter-spacing: normal !important; word-spacing: normal !important; line-height: inherit !important; margin-bottom: revert-layer; } `);
      document.documentElement.classList.add('rv-spacing-active');
    } else {
      document.documentElement.classList.remove('rv-spacing-active');
    }

    // Font size — use zoom to scale ALL text including elements with explicit px sizes
    if (fontSize !== 100) {
      const zoomVal = fontSize / 100;
      const counterZoom = (100 / fontSize).toFixed(4);
      rules.push(` html.rv-fontsize-active body { zoom: ${zoomVal} !important; } html.rv-fontsize-active .rv-toolbar, html.rv-fontsize-active .rv-tb-dropdown, html.rv-fontsize-active .rv-toggle-btn, html.rv-fontsize-active [class^="rv-mask"], html.rv-fontsize-active .rv-simplify-overlay, html.rv-fontsize-active .rv-magnifier-panel, html.rv-fontsize-active .rv-word-input-overlay, html.rv-fontsize-active .rv-ocr-overlay, html.rv-fontsize-active .rv-ocr-result, html.rv-fontsize-active .rv-dictionary-popup, html.rv-fontsize-active .rv-translation-popup, html.rv-fontsize-active .rv-context-menu { zoom: ${counterZoom} !important; } `);
      document.documentElement.classList.add('rv-fontsize-active');
    } else {
      document.documentElement.classList.remove('rv-fontsize-active');
    }

    // Font family — applied to body, cascades naturally
    if (fontFamily !== 'default') {
      const font = FONT_FAMILIES[fontFamily];
      rules.push(` html.rv-font-active body { font-family: ${font.css} !important; } html.rv-font-active body *${exclude} { font-family: inherit !important; } `);
      document.documentElement.classList.add('rv-font-active');
    } else {
      document.documentElement.classList.remove('rv-font-active');
    }

    this.styleElement.textContent = rules.join('\n');
  }

  /**
   * Destroy — remove all page modifications and restore original state
   */
  destroy(): void {
    // Deactivate all tools
    this.tools.forEach(tool => tool.destroy());
    this.tools.clear();

    // Remove page style overrides
    if (this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }

    // Remove CSS classes from html
    document.documentElement.classList.remove('rv-theme-active', 'rv-fontsize-active', 'rv-font-active');

    // Clear listeners
    this.listeners = [];
    this.wordHighlightListeners = [];
  }

  private ensureStyleElement(): void {
    if (this.styleElement) return;

    const existing = document.getElementById(STYLE_ID) as HTMLStyleElement;
    if (existing) {
      this.styleElement = existing;
      return;
    }

    this.styleElement = document.createElement('style');
    this.styleElement.id = STYLE_ID;
    document.head.appendChild(this.styleElement);
  }

  private loadSettings(): ToolbarSettings {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } as ToolbarSettings;
        // Older builds persisted the default voice ('heart') with every setting
        // change; only honour a voice the visitor explicitly chose.
        if (saved.voiceChosen !== true) {
          delete saved.voice;
          delete saved.voiceChosen;
        }
        saved.voice = normalizeVoice(saved.voice);
        return saved;
      }
    } catch {
      // Ignore parse errors
    }
    return { ...DEFAULT_SETTINGS };
  }

  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // Ignore quota errors
    }
  }

  private notifyListeners(): void {
    const snapshot = this.getSettings();
    this.listeners.forEach(cb => cb(snapshot));
  }
}
